import { Router } from '@angular/router';
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MyAuthService {
  /** Survives the Auth0 logout round-trip so /login can explain why the user was bounced. */
  private static readonly DENIAL_REASON_KEY = 'compleo.login-denial-reason';

  constructor(
    private auth0Service: Auth0Service,
    private router: Router
  ) {}

  login(): void {
    this.auth0Service.loginWithRedirect().subscribe();
  }

  signup(): void {
    this.auth0Service.loginWithRedirect().subscribe();
  }

  logout(): void {
    this.auth0Service.logout({
      logoutParams: {
        returnTo: window.location.origin
      },
    }).subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  isLogged(): Observable<boolean> {
    return this.auth0Service.isAuthenticated$;
  }

  getUser(): Observable<User | null | undefined> {
    return this.auth0Service.user$;
  }

  /** Errors surfaced by the Auth0 SDK, including a login refused by a post-login Action. */
  errors(): Observable<Error> {
    return this.auth0Service.error$;
  }

  /**
   * Terminates the Auth0 SSO session after a refused login.
   *
   * A post-login Action runs AFTER Auth0 has authenticated the user: the application is refused but
   * the SSO session is already set. Without this, every retry silently replays the same rejected
   * identity and the credentials form is never shown again -- the user cannot switch account.
   * Destroying the session is what unblocks it.
   */
  logoutAfterDenial(reason: string): void {
    sessionStorage.setItem(MyAuthService.DENIAL_REASON_KEY, reason);
    this.auth0Service.logout({
      logoutParams: {
        returnTo: window.location.origin
      },
    }).subscribe();
  }

  /** Reads and clears the reason left by the last refused login. */
  takeDenialReason(): string | null {
    const reason = sessionStorage.getItem(MyAuthService.DENIAL_REASON_KEY);
    sessionStorage.removeItem(MyAuthService.DENIAL_REASON_KEY);
    return reason;
  }
}
